import classnames from 'classnames';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Icons } from '../Icons';
import { TooltipTrigger, TooltipContent, Tooltip } from '../Tooltip';
import { Separator } from '../Separator';
import { useSystem } from '@ohif/core';
import { MobileNavigationOverlay } from './MobileNavigationOverlay';

type SidePanelProps = {
  side: 'left' | 'right';
  className?: string;
  activeTabIndex: number;
  onOpen?: () => void;
  onClose?: () => void;
  onActiveTabIndexChange?: (payload?: any) => void;
  isExpanded: boolean;
  expandedWidth?: number;
  collapsedWidth?: number;
  expandedInsideBorderSize?: number;
  collapsedInsideBorderSize?: number;
  collapsedOutsideBorderSize?: number;
  tabs: any[];

  /** Colapsar por defecto en mobile */
  collapseOnMobileDefault?: boolean;
  /** Media query para mobile */
  mobileQuery?: string;

  /** Nudge en mobile */
  mobileNudgeOnceKey?: string;
  mobileNudgeDurationMs?: number;

  /** Ocultar completamente el panel derecho en mobile */
  hideRightOnMobile?: boolean;

  /** Hacer full-screen el panel izquierdo en mobile cuando esté expandido */
  leftFullscreenOnMobile?: boolean;

  /** Selector del viewport a ocultar cuando el overlay esté activo (id, clase o tag) */
  viewportSelectorToHide?: string;
};

type StyleMap = {
  open: {
    left: { marginLeft: string; marginRight: string };
    right: { marginLeft: string; marginRight: string };
  };
  closed: {
    left: { marginLeft: string; marginRight: string; alignItems: 'flex-end' };
    right: { marginLeft: string; marginRight: string; alignItems: 'flex-start' };
  };
};

const closeIconWidth = 30;
const gridHorizontalPadding = 10;
const tabSpacerWidth = 2;

const baseClasses = 'bg-black border-black justify-start box-content flex flex-col';

const openStateIconName = {
  left: 'SidePanelCloseLeft',
  right: 'SidePanelCloseRight',
};

/** Media query hook con estado inicial seguro para SSR/CSR */
const useMediaQuery = (query: string) => {
  const getInitial = () =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches;
  const [matches, setMatches] = useState(getInitial);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      const m = 'matches' in e ? e.matches : (e as MediaQueryList).matches;
      setMatches(m);
    };
    setMatches(mql.matches);
    if ('addEventListener' in mql) mql.addEventListener('change', handler as any);
    else (mql as any).addListener(handler);
    return () => {
      if ('removeEventListener' in mql) mql.removeEventListener('change', handler as any);
      else (mql as any).removeListener(handler);
    };
  }, [query]);

  return matches;
};

const getTabWidth = (numTabs: number) => (numTabs < 3 ? 68 : 40);

const getGridWidth = (numTabs: number, gridAvailableWidth: number) => {
  const spacersWidth = (numTabs - 1) * tabSpacerWidth;
  const tabsWidth = getTabWidth(numTabs) * numTabs;
  if (gridAvailableWidth > tabsWidth + spacersWidth) return tabsWidth + spacersWidth;
  return gridAvailableWidth;
};

const getNumGridColumns = (numTabs: number, gridWidth: number) => {
  if (numTabs === 1) return 1;
  const tabWidth = getTabWidth(numTabs);
  const n = Math.floor(gridWidth / (tabWidth + tabSpacerWidth));
  if ((n + 1) * tabWidth + n * tabSpacerWidth <= gridWidth) return n + 1;
  return n;
};

const getTabClassNames = (
  numColumns: number,
  numTabs: number,
  tabIndex: number,
  isActiveTab: boolean,
  isTabDisabled: boolean
) =>
  classnames('h-[28px] mb-[2px] cursor-pointer text-white bg-black', {
    'hover:text-primary': !isActiveTab && !isTabDisabled,
    'rounded-l': tabIndex % numColumns === 0,
    'rounded-r': (tabIndex + 1) % numColumns === 0 || tabIndex === numTabs - 1,
  });

const getTabStyle = (numTabs: number) => ({ width: `${getTabWidth(numTabs)}px` });

const getTabIconClassNames = (_numTabs: number, isActiveTab: boolean) =>
  classnames('h-full w-full flex items-center justify-center', {
    'bg-customblue-40': isActiveTab,
    rounded: isActiveTab,
  });

const createStyleMap = (
  expandedWidth: number,
  expandedInsideBorderSize: number,
  collapsedWidth: number,
  collapsedInsideBorderSize: number,
  collapsedOutsideBorderSize: number
): StyleMap => {
  const collapsedHideWidth = expandedWidth - collapsedWidth - collapsedOutsideBorderSize;
  return {
    open: {
      left: { marginLeft: '0px', marginRight: `${expandedInsideBorderSize}px` },
      right: { marginLeft: `${expandedInsideBorderSize}px`, marginRight: '0px' },
    },
    closed: {
      left: {
        marginLeft: `-${collapsedHideWidth}px`,
        marginRight: `${collapsedInsideBorderSize}px`,
        alignItems: 'flex-end',
      },
      right: {
        marginLeft: `${collapsedInsideBorderSize}px`,
        marginRight: `-${collapsedHideWidth}px`,
        alignItems: 'flex-start',
      },
    },
  };
};

const getToolTipContent = (label: string, disabled: boolean) => (
  <>
    <div>{label}</div>
    {disabled && <div className="text-white">{'Not available based on current context'}</div>}
  </>
);

const createBaseStyle = (expandedWidth: number) => ({
  maxWidth: `${expandedWidth}px`,
  width: `${expandedWidth}px`,
  position: 'relative' as const,
  top: '0.2%',
  height: '99.8%',
});

const SidePanel = ({
  side,
  className,
  activeTabIndex: activeTabIndexProp,
  isExpanded,
  tabs,
  onOpen,
  onClose,
  onActiveTabIndexChange,
  expandedWidth = 280,
  collapsedWidth = 25,
  expandedInsideBorderSize = 4,
  collapsedInsideBorderSize = 8,
  collapsedOutsideBorderSize = 4,

  collapseOnMobileDefault = true,
  mobileQuery = '(max-width: 768px)',

  mobileNudgeOnceKey = 'sidepanel_mobile_nudge_shown',
  mobileNudgeDurationMs = 4000,

  hideRightOnMobile = true,

  // NUEVO: overlay full-screen para left en mobile
  leftFullscreenOnMobile = true,

  // NUEVO: selector del viewport a ocultar mientras el overlay está activo
  viewportSelectorToHide = '#cornerstone-viewport-element',
}: SidePanelProps) => {
  const isMobile = useMediaQuery(mobileQuery);

  // Navigation state for left panel
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  // Series navigation state (for collapsed arrows)
  const [seriesCurrentIndex, setSeriesCurrentIndex] = useState(0);
  const [seriesTotal, setSeriesTotal] = useState(0);
  const [seriesDisplaySets, setSeriesDisplaySets] = useState<any[]>([]);

  // Get services for image navigation (only when side === 'left')
  const systemContext = side === 'left' ? useSystem() : null;

  // Ocultar completamente el Right en mobile (se evita reservar espacio)
  if (hideRightOnMobile && isMobile && side === 'right') {
    return null;
  }

  const [panelOpen, setPanelOpen] = useState<boolean>(isExpanded);
  const [userInteracted, setUserInteracted] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(activeTabIndexProp ?? 0);

  const [styleMap, setStyleMap] = useState(
    createStyleMap(
      expandedWidth,
      expandedInsideBorderSize,
      collapsedWidth,
      collapsedInsideBorderSize,
      collapsedOutsideBorderSize
    )
  );
  const [baseStyle, setBaseStyle] = useState(createBaseStyle(expandedWidth));

  const [gridAvailableWidth, setGridAvailableWidth] = useState(
    expandedWidth - closeIconWidth - gridHorizontalPadding
  );
  const [gridWidth, setGridWidth] = useState(getGridWidth(tabs.length, gridAvailableWidth));

  // ---- NUDGE/TOOLTIP EN MOBILE (FIXED) ----
  const [showMobileNudge, setShowMobileNudge] = useState(false);
  const arrowRef = useRef<HTMLDivElement | null>(null);
  const [nudgePos, setNudgePos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const openStatus = panelOpen ? 'open' : 'closed';
  const style = useMemo(
    () => Object.assign({}, styleMap[openStatus][side], baseStyle) as React.CSSProperties,
    [styleMap, openStatus, side, baseStyle]
  );

  const updatePanelOpen = useCallback(
    (isOpen: boolean, fromUser = false) => {
      setPanelOpen(prev => {
        if (prev === isOpen) return prev;
        if (isOpen && onOpen) onOpen();
        if (!isOpen && onClose) onClose();
        return isOpen;
      });
      if (fromUser) {
        setUserInteracted(true);
        setShowMobileNudge(false);
        if (typeof window !== 'undefined') {
          try {
            sessionStorage.setItem(mobileNudgeOnceKey, '1');
          } catch {}
        }
      }
    },
    [onOpen, onClose, mobileNudgeOnceKey]
  );

  const updateActiveTabIndex = useCallback(
    (index: number, forceOpen: boolean = false) => {
      if (forceOpen) updatePanelOpen(true, true);
      setActiveTabIndex(index);
      onActiveTabIndexChange?.({ activeTabIndex: index });
    },
    [onActiveTabIndexChange, updatePanelOpen]
  );

  // Sincroniza estilos/base
  useEffect(() => {
    setStyleMap(
      createStyleMap(
        expandedWidth,
        expandedInsideBorderSize,
        collapsedWidth,
        collapsedInsideBorderSize,
        collapsedOutsideBorderSize
      )
    );
    setBaseStyle(createBaseStyle(expandedWidth));
    const gw = expandedWidth - closeIconWidth - gridHorizontalPadding;
    setGridAvailableWidth(gw);
    setGridWidth(getGridWidth(tabs.length, gw));
  }, [
    collapsedInsideBorderSize,
    collapsedWidth,
    expandedWidth,
    expandedInsideBorderSize,
    tabs.length,
    collapsedOutsideBorderSize,
  ]);

  // Índice activo desde props
  useEffect(() => {
    updateActiveTabIndex(activeTabIndexProp ?? 0);
  }, [activeTabIndexProp, updateActiveTabIndex]);

  // Responsive: colapsado por defecto en mobile; desktop respeta isExpanded
  useEffect(() => {
    if (collapseOnMobileDefault && isMobile) {
      if (!userInteracted) updatePanelOpen(false, false);
    } else {
      updatePanelOpen(isExpanded, false);
      setUserInteracted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, collapseOnMobileDefault, isExpanded]);

  useEffect(() => {
    if (!isMobile) updatePanelOpen(isExpanded, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, isMobile]);

  // Calcular posición del nudge (fixed, junto a la flecha)
  const recalcNudge = useCallback(() => {
    if (!arrowRef.current) return;
    const rect = arrowRef.current.getBoundingClientRect();
    const offsetX = 12; // offset a la derecha de la flecha
    const centerY = rect.top + rect.height / 2;
    setNudgePos({
      top: Math.max(8, centerY - 16),
      left: rect.right + offsetX,
    });
  }, []);

  useLayoutEffect(() => {
    if (showMobileNudge) {
      recalcNudge();
      const onScroll = () => recalcNudge();
      const onResize = () => recalcNudge();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResize);
      return () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
      };
    }
  }, [showMobileNudge, recalcNudge]);

  // Mostrar nudge solo mobile, panel cerrado, lado izquierdo, una vez por sesión
  useEffect(() => {
    if (!isMobile || panelOpen || side !== 'left') {
      setShowMobileNudge(false);
      return;
    }
    let alreadyShown = false;
    if (typeof window !== 'undefined') {
      try {
        alreadyShown = sessionStorage.getItem(mobileNudgeOnceKey) === '1';
      } catch {}
    }
    if (!alreadyShown) {
      setShowMobileNudge(true);
      const t = setTimeout(() => setShowMobileNudge(false), mobileNudgeDurationMs);
      return () => clearTimeout(t);
    }
  }, [isMobile, panelOpen, side, mobileNudgeOnceKey, mobileNudgeDurationMs]);

  /** --------- OVERLAY FULL-SCREEN & HIDE VIEWPORT (mobile + left + open) ---------- */
  const overlayActive = leftFullscreenOnMobile && isMobile && side === 'left' && panelOpen;

  // Evitar scroll del body cuando el overlay está activo
  useEffect(() => {
    if (!overlayActive) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [overlayActive]);

  // Ocultar el viewport (Cornerstone) mientras el overlay está activo
  useEffect(() => {
    if (!viewportSelectorToHide) return;
    let target: HTMLElement | null = null;
    let prevDisplay = '';
    if (overlayActive) {
      target =
        (document.querySelector(viewportSelectorToHide) as HTMLElement) ||
        (document.querySelector('cornerstone-viewport-element') as HTMLElement) || // por si fuese tag
        null;
      if (target) {
        prevDisplay = target.style.display;
        target.style.display = 'none';
      }
    }
    return () => {
      if (target) {
        target.style.display = prevDisplay || '';
      }
    };
  }, [overlayActive, viewportSelectorToHide]);

  // Estilo base normal vs overlay a pantalla completa
  const computedContainerStyle = overlayActive
    ? {
        position: 'fixed' as const,
        inset: 0,
        width: '100vw',
        maxWidth: '100vw',
        height: '100vh',
        top: 0,
        zIndex: 10000, // por encima del viewport y otros elementos
        display: 'flex',
        flexDirection: 'column' as const,
      }
    : style;

  /** ------------------------------------------------------------------------------ */

  /** --------- IMAGE NAVIGATION (only for left panel) ---------- */
  // Track current image index when side is left
  useEffect(() => {
    if (side !== 'left' || !systemContext) return;

    const { servicesManager } = systemContext;
    const { viewportGridService } = servicesManager.services;

    // Type assertion for cornerstone viewport service (from cornerstone extension)
    const cornerstoneViewportService = (servicesManager.services as any).cornerstoneViewportService;

    const updateImageInfo = () => {
      try {
        const activeViewportId = viewportGridService.getActiveViewportId();
        if (!activeViewportId || !cornerstoneViewportService) return;

        const viewport = cornerstoneViewportService.getCornerstoneViewport(activeViewportId);
        if (!viewport) return;

        const currentIndex = viewport.getCurrentImageIdIndex();
        const numberOfSlices = viewport.getNumberOfSlices();

        setCurrentImageIndex(currentIndex);
        setTotalImages(numberOfSlices);
      } catch (error) {
        // Viewport might not be ready or might be a 3D viewport
        setCurrentImageIndex(0);
        setTotalImages(0);
      }
    };

    // Update on viewport change
    const subscription = viewportGridService.subscribe(
      viewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED,
      updateImageInfo
    );

    // Initial update
    updateImageInfo();

    // Set up polling to update image index regularly
    const pollInterval = setInterval(updateImageInfo, 200);

    return () => {
      subscription.unsubscribe();
      clearInterval(pollInterval);
    };
  }, [side, systemContext]);

  // Navigation handlers
  const handleNavigatePrevious = useCallback(() => {
    if (side !== 'left' || !systemContext) return;
    console.log('handleNavigateePrevious');
    const { commandsManager } = systemContext;
    commandsManager.runCommand('scroll', { direction: -1, delta: -1 });
  }, [side, systemContext]);

  const handleNavigateNext = useCallback(() => {
    if (side !== 'left' || !systemContext) return;
    console.log('handleNavigateNext');
    const { commandsManager } = systemContext;
    commandsManager.runCommand('scroll', { direction: 1, delta: 1 });
  }, [side, systemContext]);

  /** --------- SERIES NAVIGATION (collapsed left panel) ---------- */
  // Build list of series for the active viewport and track current index
  useEffect(() => {
    if (side !== 'left' || !systemContext) return;

    const { servicesManager } = systemContext;
    const { viewportGridService, displaySetService } = servicesManager.services as any;

    const updateSeriesInfo = () => {
      try {
        const activeViewportId = viewportGridService.getActiveViewportId();
        if (!activeViewportId) {
          setSeriesCurrentIndex(0);
          setSeriesTotal(0);
          setSeriesDisplaySets([]);
          return;
        }

        const viewportDisplaySetUIDs =
          viewportGridService.getDisplaySetsUIDsForViewport(activeViewportId);
        const primaryUID = viewportDisplaySetUIDs?.[0];
        if (!primaryUID) {
          setSeriesCurrentIndex(0);
          setSeriesTotal(0);
          setSeriesDisplaySets([]);
          return;
        }

        const primary = displaySetService.getDisplaySetByUID(primaryUID);
        if (!primary) return;

        // Always filter by current StudyInstanceUID only and exclude non-image/unsupported
        const all = displaySetService.getActiveDisplaySets();
        const nonImageModalities = [
          'SR',
          'SEG',
          'SM',
          'RTSTRUCT',
          'RTPLAN',
          'RTDOSE',
          'DOC',
          'PMAP',
        ];
        const allSeriesInStudy = all.filter(
          (ds: any) =>
            ds.StudyInstanceUID === primary.StudyInstanceUID &&
            !nonImageModalities.includes(ds.Modality) &&
            !ds?.unsupported
        );
        // Unique by SeriesInstanceUID, order by SeriesNumber
        const seriesMap = new Map();
        allSeriesInStudy.forEach((ds: any) => {
          const key = ds.SeriesInstanceUID;
          if (!seriesMap.has(key)) {
            seriesMap.set(key, ds);
          }
        });
        const uniqueSeriesDisplaySets = Array.from(seriesMap.values());
        uniqueSeriesDisplaySets.sort(
          (a: any, b: any) => (a.SeriesNumber || 0) - (b.SeriesNumber || 0)
        );
        const idx = uniqueSeriesDisplaySets.findIndex(
          (ds: any) =>
            ds.displaySetInstanceUID === primaryUID ||
            ds.SeriesInstanceUID === primary.SeriesInstanceUID
        );
        setSeriesCurrentIndex(idx >= 0 ? idx : 0);
        setSeriesTotal(uniqueSeriesDisplaySets.length);
        setSeriesDisplaySets(uniqueSeriesDisplaySets);
      } catch (e) {
        console.error('Error updating series info:', e);
        setSeriesCurrentIndex(0);
        setSeriesTotal(0);
        setSeriesDisplaySets([]);
      }
    };

    const sub1 = servicesManager.services.viewportGridService.subscribe(
      servicesManager.services.viewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED,
      updateSeriesInfo
    );
    const sub1b = servicesManager.services.viewportGridService.subscribe(
      servicesManager.services.viewportGridService.EVENTS.GRID_STATE_CHANGED,
      updateSeriesInfo
    );
    const sub2 = servicesManager.services.displaySetService.subscribe(
      servicesManager.services.displaySetService.EVENTS.DISPLAY_SETS_CHANGED,
      updateSeriesInfo
    );

    updateSeriesInfo();
    return () => {
      sub1?.unsubscribe?.();
      sub2?.unsubscribe?.();
      sub1b?.unsubscribe?.();
    };
  }, [side, systemContext]);

  // Reuse command to navigate to a specific displaySetInstanceUID
  const navigateToDisplaySet = useCallback(
    (displaySetInstanceUID: string) => {
      if (!systemContext || !displaySetInstanceUID) return;
      const { commandsManager } = systemContext;
      // Use run for consistency with rest of app
      commandsManager.run('navigateToDisplaySet', { displaySetInstanceUID });
    },
    [systemContext]
  );

  // Expose reusable selection by absolute index
  const selectByIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= seriesTotal) return;
      const target = seriesDisplaySets[index];
      if (!target) return;
      navigateToDisplaySet(target.displaySetInstanceUID);
    },
    [seriesDisplaySets, seriesTotal, navigateToDisplaySet]
  );

  /** ------------------------------------------------------------------------------ */

  const getCloseStateComponent = () => {
    const _childComponents = Array.isArray(tabs) ? tabs : [tabs];

    // Small reusable mobile series nav buttons used in multiple places
    const MobileSeriesNavButtons = ({
      orientation = 'horizontal',
      size = 35,
      className = '',
    }: {
      orientation?: 'horizontal' | 'vertical';
      size?: number;
      className?: string;
    }) => {
      const btnBase = `tt flex items-center justify-center rounded border bg-black/70 backdrop-blur`;
      const wh = { width: `${size}px`, height: `${size}px` } as React.CSSProperties;
      const disabledPrev = seriesCurrentIndex <= 0;
      const disabledNext = seriesCurrentIndex >= seriesTotal - 1;
      const stackCls = orientation === 'vertical' ? 'flex-col gap-1' : 'flex-row gap-2';

      return (
        <div className={`flex ${stackCls} ${className}`}>
          <button
            type="button"
            data-cy="mobile-inline-series-prev"
            onClick={() => selectByIndex(seriesCurrentIndex - 1)}
            disabled={disabledPrev}
            aria-label="Previous series"
            aria-disabled={disabledPrev}
            className={classnames(
              btnBase,
              disabledPrev
                ? 'cursor-not-allowed border-gray-700 text-gray-500'
                : 'border-primary-dark text-primary hover:border-primary-light'
            )}
            style={wh}
          >
            <Icons.ArrowLeftBold className="h-8 w-8" />
          </button>
          <button
            type="button"
            data-cy="mobile-inline-series-next"
            onClick={() => selectByIndex(seriesCurrentIndex + 1)}
            disabled={disabledNext}
            aria-label="Next series"
            aria-disabled={disabledNext}
            className={classnames(
              btnBase,
              disabledNext
                ? 'cursor-not-allowed border-gray-700 text-gray-500'
                : 'border-primary-dark text-primary hover:border-primary-light'
            )}
            style={wh}
          >
            <Icons.ArrowRightBold className="h-8 w-8" />
          </button>
        </div>
      );
    };

    const ArrowButton = (
      <div
        ref={arrowRef}
        className={classnames(
          'bg-secondary-dark flex h-[28px] w-full cursor-pointer items-center rounded-md',
          side === 'left' ? 'justify-end pr-2' : 'justify-start pl-2'
        )}
        onClick={() => updatePanelOpen(!panelOpen, true)}
        data-cy={`side-panel-header-${side}`}
      >
        <Icons.NavigationPanelReveal
          className={classnames('text-primary', side === 'left' && 'rotate-180 transform')}
        />
      </div>
    );

    return (
      <>
        {/* Botón de flecha */}
        {ArrowButton}

        {/* Collapsed prev/next INSIDE panel, only mobile (stacked vertically under toggle) */}
        {isMobile && side === 'left' && seriesTotal > 1 && (
          <MobileSeriesNavButtons
            orientation="vertical"
            size={24}
            className="mt-2 self-end md:hidden"
          />
        )}

        {/* Tooltip/Nudge FIXED solo en mobile, izquierdo y panel cerrado */}
        {showMobileNudge && isMobile && side === 'left' && (
          <div
            role="tooltip"
            aria-live="polite"
            className="pointer-events-none select-none whitespace-nowrap rounded-md bg-black/90 px-3 py-1 text-sm font-medium text-white shadow-lg"
            style={{
              position: 'fixed',
              top: `${nudgePos.top}px`,
              left: `${nudgePos.left}px`,
              zIndex: 10000,
            }}
          >
            <div className="flex items-center gap-1">
              <Icons.ArrowLeft className="h-4 w-4 shrink-0" />
              <span>Lista de estudios</span>
            </div>
          </div>
        )}

        <div className={classnames('mt-3 flex flex-col space-y-3')}>
          {_childComponents.map((childComponent, index) => (
            <Tooltip key={index}>
              <TooltipTrigger>
                <div
                  id={`${childComponent.name}-btn`}
                  data-cy={`${childComponent.name}-btn`}
                  className="text-primary hover:cursor-pointer"
                  onClick={() =>
                    childComponent.disabled ? null : updateActiveTabIndex(index, true)
                  }
                >
                  {React.createElement(Icons[childComponent.iconName] || Icons.MissingIcon, {
                    className: classnames({
                      'text-primary': true,
                      'ohif-disabled': childComponent.disabled,
                    }),
                    style: { width: '22px', height: '22px' },
                  })}
                </div>
              </TooltipTrigger>
              <TooltipContent side={side === 'left' ? 'right' : 'left'}>
                <div
                  className={classnames(
                    'flex items-center',
                    side === 'left' ? 'justify-end' : 'justify-start'
                  )}
                >
                  {getToolTipContent(childComponent.label, childComponent.disabled)}
                </div>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </>
    );
  };

  const getCloseIcon = () => (
    <div
      className={classnames(
        'absolute flex cursor-pointer items-center justify-center',
        side === 'left' ? 'right-0' : 'left-0'
      )}
      style={{ width: `${closeIconWidth}px` }}
      onClick={() => updatePanelOpen(!panelOpen, true)}
      data-cy={`side-panel-header-${side}`}
    >
      {React.createElement(Icons[openStateIconName[side]] || Icons.MissingIcon, {
        className: 'text-primary',
      })}
    </div>
  );

  const getTabGridComponent = () => {
    const numCols = getNumGridColumns(tabs.length, gridWidth);
    return (
      <>
        {getCloseIcon()}
        <div className={classnames('flex grow justify-center')}>
          <div className={classnames('bg-primary-dark text-primary flex flex-wrap')}>
            {tabs.map((tab, tabIndex) => {
              const { disabled } = tab;
              return (
                <React.Fragment key={tabIndex}>
                  {tabIndex % numCols !== 0 && (
                    <div
                      className={classnames(
                        'flex h-[28px] w-[2px] items-center bg-black',
                        tabSpacerWidth
                      )}
                    >
                      <div className="bg-primary-dark h-[20px] w-full"></div>
                    </div>
                  )}
                  <Tooltip key={tabIndex}>
                    <TooltipTrigger>
                      <div
                        className={getTabClassNames(
                          numCols,
                          tabs.length,
                          tabIndex,
                          tabIndex === activeTabIndex,
                          disabled
                        )}
                        style={getTabStyle(tabs.length)}
                        onClick={() => (disabled ? null : updateActiveTabIndex(tabIndex))}
                        data-cy={`${tab.name}-btn`}
                      >
                        <div
                          className={getTabIconClassNames(tabs.length, tabIndex === activeTabIndex)}
                        >
                          {React.createElement(Icons[tab.iconName] || Icons.MissingIcon, {
                            className: classnames({
                              'text-primary': true,
                              'ohif-disabled': disabled,
                            }),
                            style: { width: '22px', height: '22px' },
                          })}
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {getToolTipContent(tab.label, disabled)}
                    </TooltipContent>
                  </Tooltip>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  const getOneTabComponent = () => (
    <div
      className={classnames(
        'text-primary flex grow cursor-pointer select-none justify-center self-center text-[13px]'
      )}
      data-cy={`${tabs[0].name}-btn`}
      onClick={() => updatePanelOpen(!panelOpen, true)}
    >
      {getCloseIcon()}
      <span>{tabs[0].label}</span>
    </div>
  );

  const getOpenStateComponent = () => (
    <>
      <div className="bg-bkg-med flex h-[40px] flex-shrink-0 select-none rounded-t p-2">
        {tabs.length === 1 ? getOneTabComponent() : getTabGridComponent()}
      </div>
      <Separator
        orientation="horizontal"
        className="bg-black"
        thickness="2px"
      />
    </>
  );

  return (
    <>
      <div
        className={classnames(className, baseClasses)}
        style={computedContainerStyle}
      >
        {panelOpen ? (
          <>
            {getOpenStateComponent()}
            {tabs.map((tab: any, tabIndex: number) =>
              tabIndex === activeTabIndex ? <tab.content key={tabIndex} /> : null
            )}

            {/* Navigation arrows - Desktop only, inside panel
            {side === 'left' && !overlayActive && totalImages > 0 && (
              <div className="border-primary-dark flex items-center justify-between border-t bg-black px-2 py-3">
                <button
                  onClick={handleNavigatePrevious}
                  disabled={currentImageIndex <= 0}
                  className={classnames(
                    'flex h-10 w-10 items-center justify-center rounded border',
                    currentImageIndex <= 0
                      ? 'cursor-not-allowed border-gray-700 text-gray-600'
                      : 'border-primary-dark text-primary hover:border-primary-light hover:bg-primary-dark'
                  )}
                  title={`Previous image (${currentImageIndex} / ${totalImages - 1})`}
                >
                  <Icons.ArrowLeftBold className="h-8 w-8" />
                </button>

                <span className="text-primary text-xs">
                  {currentImageIndex + 1} / {totalImages}
                </span>

                <button
                  onClick={handleNavigateNext}
                  disabled={currentImageIndex >= totalImages - 1}
                  className={classnames(
                    'flex h-10 w-10 items-center justify-center rounded border',
                    currentImageIndex >= totalImages - 1
                      ? 'cursor-not-allowed border-gray-700 text-gray-600'
                      : 'border-primary-dark text-primary hover:border-primary-light hover:bg-primary-dark'
                  )}
                  title={`Next image (${currentImageIndex + 2} / ${totalImages})`}
                >
                  <Icons.ArrowRightBold className="h-8 w-8" />
                </button>
              </div>
            )}*/}
          </>
        ) : (
          getCloseStateComponent()
        )}
      </div>
      {/** Mobile floating series navigation (always visible on mobile, left side) */}
      {isMobile && side === 'left' && seriesTotal > 1 && (
        <div
          role="group"
          aria-label="Series navigation"
          className="flex items-center gap-3 md:hidden"
          style={{
            position: 'fixed',
            left: 8,
            top: '40vh',
            zIndex: 10020,
          }}
        >
          <button
            type="button"
            data-cy="mobile-series-prev"
            onClick={() => selectByIndex(seriesCurrentIndex - 1)}
            disabled={seriesCurrentIndex <= 0}
            aria-label="Previous series"
            aria-disabled={seriesCurrentIndex <= 0}
            className={classnames(
              'flex h-12 w-12 items-center justify-center rounded-full border bg-black/80 text-primary shadow-lg ring-1 ring-black/60 backdrop-blur-sm',
              seriesCurrentIndex <= 0
                ? 'cursor-not-allowed border-gray-700 text-gray-500'
                : 'border-primary-dark hover:border-primary-light'
            )}
          >
            <Icons.ArrowLeftBold className="h-7 w-7" />
          </button>
          <button
            type="button"
            data-cy="mobile-series-next"
            onClick={() => selectByIndex(seriesCurrentIndex + 1)}
            disabled={seriesCurrentIndex >= seriesTotal - 1}
            aria-label="Next series"
            aria-disabled={seriesCurrentIndex >= seriesTotal - 1}
            className={classnames(
              'flex h-12 w-12 items-center justify-center rounded-full border bg-black/80 text-primary shadow-lg ring-1 ring-black/60 backdrop-blur-sm',
              seriesCurrentIndex >= seriesTotal - 1
                ? 'cursor-not-allowed border-gray-700 text-gray-500'
                : 'border-primary-dark hover:border-primary-light'
            )}
          >
            <Icons.ArrowRightBold className="h-7 w-7" />
          </button>
        </div>
      )}
      {/* Mobile overlay for left panel (outside the main panel div) */}
      {overlayActive && (
        <MobileNavigationOverlay onClose={() => updatePanelOpen(false, true)}>
          {getOpenStateComponent()}
          {tabs.map((tab: any, tabIndex: number) =>
            tabIndex === activeTabIndex ? <tab.content key={tabIndex} /> : null
          )}
        </MobileNavigationOverlay>
      )}
    </>
  );
};

export { SidePanel };
